import {
  type CatalogNodeKey,
  type VestiaireCatalogNodeLike,
  catalogCriteriaToPaths,
  countMissingCatalogNodes,
  pathsToCatalogCriteria,
} from '@/features/productSearch/vestiaire/catalogTree';
import type {
  KeywordSearchOperator,
  VestiaireItemBadge,
  VestiaireSearchCriteriaInput,
  VestiaireSellerBadge,
} from '@/shared/api/types/generated';
import { itemBadgeList, numberList, sellerBadgeList, stringList } from '@/shared/utils/analysis/vestiaireFilterValues';

// ============================================================================
// Formulaire de recherche Vestiaire ⇄ VestiaireSearchCriteriaInput ⇄ vestiaireFilter
// stocké (mêmes clés que le critère). Validation miroir de celle du serveur, pour ne
// pas envoyer une requête coûteuse vouée au 400.
// ============================================================================

export interface VestiaireFormState {
  catalogPaths: CatalogNodeKey[][];
  brandIds: number[];
  excludedBrandIds: number[];
  modelIds: number[];
  conditionIds: number[];
  sizeIds: number[];
  colorIds: number[];
  materialIds: number[];
  countryIds: number[];
  sellerBadges: VestiaireSellerBadge[];
  itemBadges: VestiaireItemBadge[];
  priceMin: string;
  priceMax: string;
  keywords: string[];
  keywordsOperator: KeywordSearchOperator;
  excludedKeywords: string[];
}

export const EMPTY_VESTIAIRE_FORM: VestiaireFormState = {
  catalogPaths: [],
  brandIds: [],
  excludedBrandIds: [],
  modelIds: [],
  conditionIds: [],
  sizeIds: [],
  colorIds: [],
  materialIds: [],
  countryIds: [],
  sellerBadges: [],
  itemBadges: [],
  priceMin: '',
  priceMax: '',
  keywords: [],
  keywordsOperator: 'and',
  excludedKeywords: [],
};

const ID_FIELDS = [
  'brandIds',
  'excludedBrandIds',
  'modelIds',
  'conditionIds',
  'sizeIds',
  'colorIds',
  'materialIds',
  'countryIds',
] as const;

const NARROWING_ID_FIELDS = [
  'universeIds',
  'catalogRootIds',
  'catalogLevel1Ids',
  'catalogLevel2Ids',
  'brandIds',
  'modelIds',
  'conditionIds',
  'sizeIds',
  'colorIds',
  'materialIds',
] as const;

// Mots saisis tels quels : `%` et `_` ne sont PAS échappés (parité voulue avec Vinted).
const cleanWords = (words: string[]): string[] => words.map((word) => word.trim()).filter((word) => word !== '');

const parsePrice = (value: string): number | undefined => (value.trim() === '' ? undefined : Number(value));

export const buildVestiaireCriteria = (form: VestiaireFormState): VestiaireSearchCriteriaInput => {
  const criteria: VestiaireSearchCriteriaInput = { ...pathsToCatalogCriteria(form.catalogPaths) };
  for (const field of ID_FIELDS) {
    if (form[field].length) criteria[field] = form[field];
  }
  if (form.sellerBadges.length) criteria.sellerBadges = form.sellerBadges;
  if (form.itemBadges.length) criteria.itemBadges = form.itemBadges;
  const priceMin = parsePrice(form.priceMin);
  const priceMax = parsePrice(form.priceMax);
  if (priceMin !== undefined) criteria.priceMin = priceMin;
  if (priceMax !== undefined) criteria.priceMax = priceMax;
  const keywords = cleanWords(form.keywords);
  if (keywords.length) criteria.keywords = keywords;
  if (keywords.length > 1 && form.keywordsOperator === 'or') {
    criteria.keywordsOperator = 'or';
  }
  const excluded = cleanWords(form.excludedKeywords);
  if (excluded.length) criteria.excludedKeywords = excluded;
  return criteria;
};

export const hasNarrowingCriterion = (criteria: VestiaireSearchCriteriaInput): boolean =>
  NARROWING_ID_FIELDS.some((field) => (criteria[field]?.length ?? 0) > 0) ||
  criteria.priceMin != null ||
  criteria.priceMax != null ||
  (criteria.keywords?.length ?? 0) > 0;

// Bornes miroir du serveur (apps/api/src/vestiaire/utils/vestiaire-filter.ts) : au-delà, 400.
const MAX_IDS_PER_CRITERION = 500;
const MAX_KEYWORDS = 20;
const MAX_KEYWORD_LENGTH = 100;

// Tous les champs d'ids du critère envoyé (catégories, pays et marques exclues compris), bornés chacun.
// Les marques exclues ne RESSERRENT pas (absentes de NARROWING_ID_FIELDS, comme côté serveur).
const BOUNDED_ID_FIELDS = [...NARROWING_ID_FIELDS, 'countryIds', 'excludedBrandIds'] as const;

export type VestiaireKeywordError = 'tooMany' | 'tooLong';

export interface VestiaireFormErrors {
  priceMin?: 'invalid' | 'range';
  criteria?: 'notNarrowing';
  keywords?: VestiaireKeywordError;
  excludedKeywords?: VestiaireKeywordError;
  ids?: 'tooMany';
}

/** L'aperçu ne part (appel coûteux) que sans AUCUNE erreur — bornes serveur comprises. */
export const hasBlockingVestiaireError = (errors: VestiaireFormErrors): boolean =>
  Object.values(errors).some((error) => error !== undefined);

/**
 * La CRÉATION ne bloque, comme sur Vinted (SearchModal.validate), que sur le prix et le critère
 * resserrant : les bornes serveur (mots-clés, ids) ne sont plus vérifiées côté client à la
 * création — le serveur répond, et l'erreur de création s'affiche par le même chemin que Vinted.
 */
export const hasBlockingVestiaireSubmitError = (errors: VestiaireFormErrors): boolean =>
  errors.priceMin !== undefined || errors.criteria !== undefined;

const keywordError = (words: string[] | null | undefined): VestiaireKeywordError | undefined => {
  if ((words?.length ?? 0) > MAX_KEYWORDS) return 'tooMany';
  if (words?.some((word) => word.length > MAX_KEYWORD_LENGTH)) return 'tooLong';
  return undefined;
};

export const validateVestiaireForm = (form: VestiaireFormState): VestiaireFormErrors => {
  const errors: VestiaireFormErrors = {};
  const priceMin = parsePrice(form.priceMin);
  const priceMax = parsePrice(form.priceMax);
  const invalid = (value: number | undefined) => value !== undefined && (!Number.isFinite(value) || value < 0);
  if (invalid(priceMin) || invalid(priceMax)) errors.priceMin = 'invalid';
  else if (priceMin !== undefined && priceMax !== undefined && priceMin > priceMax) errors.priceMin = 'range';
  const criteria = buildVestiaireCriteria(form);
  if (!hasNarrowingCriterion(criteria)) errors.criteria = 'notNarrowing';
  // Sur les mots NETTOYÉS, ceux réellement envoyés : un mot vide n'est jamais compté.
  const keywords = keywordError(criteria.keywords);
  if (keywords) errors.keywords = keywords;
  const excludedKeywords = keywordError(criteria.excludedKeywords);
  if (excludedKeywords) errors.excludedKeywords = excludedKeywords;
  if (BOUNDED_ID_FIELDS.some((field) => (criteria[field]?.length ?? 0) > MAX_IDS_PER_CRITERION)) {
    errors.ids = 'tooMany';
  }
  return errors;
};

const catalogCriteriaFromFilter = (filter: Record<string, unknown>) => ({
  universeIds: numberList(filter.universeIds),
  catalogRootIds: numberList(filter.catalogRootIds),
  catalogLevel1Ids: numberList(filter.catalogLevel1Ids),
  catalogLevel2Ids: numberList(filter.catalogLevel2Ids),
});

/** Catégories du filtre stocké absentes de l'arbre actuel : écartées du formulaire, à signaler. */
export const countMissingFilterCategories = (
  filter: Record<string, unknown> | null | undefined,
  tree: VestiaireCatalogNodeLike[],
): number => (filter ? countMissingCatalogNodes(catalogCriteriaFromFilter(filter), tree) : 0);

/** vestiaireFilter stocké (ou vestiaireRelaunchCriteria) → état du formulaire (édition, lecture seule). */
export const vestiaireFilterToForm = (
  filter: Record<string, unknown> | null | undefined,
  tree: VestiaireCatalogNodeLike[],
): VestiaireFormState => {
  if (!filter) return EMPTY_VESTIAIRE_FORM;
  const price = (value: unknown) => (typeof value === 'number' ? String(value) : '');
  return {
    catalogPaths: catalogCriteriaToPaths(catalogCriteriaFromFilter(filter), tree),
    brandIds: numberList(filter.brandIds),
    excludedBrandIds: numberList(filter.excludedBrandIds),
    modelIds: numberList(filter.modelIds),
    conditionIds: numberList(filter.conditionIds),
    sizeIds: numberList(filter.sizeIds),
    colorIds: numberList(filter.colorIds),
    materialIds: numberList(filter.materialIds),
    countryIds: numberList(filter.countryIds),
    sellerBadges: sellerBadgeList(filter.sellerBadges),
    itemBadges: itemBadgeList(filter.itemBadges),
    priceMin: price(filter.priceMin),
    priceMax: price(filter.priceMax),
    keywords: stringList(filter.keywords),
    keywordsOperator: filter.keywordsOperator === 'or' ? 'or' : 'and',
    excludedKeywords: stringList(filter.excludedKeywords),
  };
};
